import { Button } from "@kopi/ui/components/button";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCheckIcon } from "lucide-react";
import { useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import {
  ApproveShiftDialog,
  CashBreakdownList,
  LateSalesList,
  MovementList,
  PendingQrisList,
  ShiftFigures,
  ShiftStatusBadge,
} from "@/components/shifts";
import { shifts } from "@/i18n/messages/shifts";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { meScopesQuery, shiftQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/shifts/$shiftId")({
  staticData: {
    section: "workspace",
    parent: { label: "nav.shifts", to: "/shifts" },
    crumb: "nav.shift",
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(shiftQuery(params.shiftId)),
  component: ShiftDetailPage,
});

/** One shift for a manager (R1-15): figures, movements to review, and the close to approve. */
function ShiftDetailPage() {
  const { shiftId } = Route.useParams();
  const { t, format } = useMessages(shifts);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: shift } = useSuspenseQuery(shiftQuery(shiftId));
  const [approving, setApproving] = useState(false);
  const mayApprove = canAt(scopes, "cash.shift.approve", shift.outletId);
  // A closed shift reads as its report recorded it: names as they were at the close (R1-30).
  const report = shift.closeReport;
  const registerName = report?.register.name ?? shift.registerName;
  const openedBy = report?.openedBy.name ?? shift.openedBy;
  const approvedBy = report?.approvedBy.name ?? shift.approvedBy;
  const closeRequestedBy = report?.closeRequestedBy.name ?? shift.closeRequestedBy;
  const notes = [
    shift.closeRequestedAt && closeRequestedBy
      ? t("shifts.closeRequestedBy", {
          time: format.dateTime(shift.closeRequestedAt),
          name: closeRequestedBy,
        })
      : null,
    shift.closedAt && approvedBy
      ? t("shifts.approvedBy", { time: format.dateTime(shift.closedAt), name: approvedBy })
      : null,
  ].filter((note) => note !== null);

  return (
    <Page>
      <PageHeader
        eyebrow={t("shifts.ofRegister", { register: registerName, number: shift.number })}
        title={t("shifts.number", { number: shift.number })}
        subtitle={t("shifts.openedBy", { time: format.dateTime(shift.openedAt), name: openedBy })}
        actions={
          <>
            <ShiftStatusBadge status={shift.status} />
            {shift.status === "closing" && mayApprove && (
              <Button type="button" onClick={() => setApproving(true)}>
                <CheckCheckIcon />
                {t("shifts.approve")}
              </Button>
            )}
          </>
        }
      />
      <ShiftFigures shift={shift} />
      <CashBreakdownList shift={shift} />
      <PendingQrisList shift={shift} />
      <LateSalesList shift={shift} />
      {(notes.length > 0 || shift.closingNote || shift.approvalNote) && (
        <dl className="grid gap-3 rounded-xl border bg-card p-4 text-sm sm:grid-cols-2">
          {notes.map((note) => (
            <div key={note} className="text-muted-foreground sm:col-span-2">
              <dd>{note}</dd>
            </div>
          ))}
          {shift.closingNote && (
            <div>
              <dt className="font-medium">{t("shifts.closingNote")}</dt>
              <dd className="break-words">{shift.closingNote}</dd>
            </div>
          )}
          {shift.approvalNote && (
            <div>
              <dt className="font-medium">{t("shifts.approvalNote")}</dt>
              <dd className="break-words">{shift.approvalNote}</dd>
            </div>
          )}
        </dl>
      )}
      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-lg">{t("shifts.movements")}</h2>
        <MovementList shift={shift} reviewable={mayApprove} />
      </section>
      {approving && <ApproveShiftDialog shift={shift} onClose={() => setApproving(false)} />}
    </Page>
  );
}
